(function () {
  'use strict';

  /** Dieux engagement analytics: section views and time, PDP scroll depth, variant, gallery, tab, accordion, sticky add-to-cart, and video events. */

  if (window.Shopify && window.Shopify.designMode) return;
  if (window.__dieuxEngagementEvents) return;
  window.__dieuxEngagementEvents = true;

  const VIEW_MIN_MS = 1000;
  const TIME_MIN_MS = 1000;
  const GESTURE_WINDOW_MS = 1500;
  const SCROLL_MILESTONES = [25, 50, 75, 90];
  const VIDEO_MILESTONES = [25, 50, 75];
  const SECTION_ID_PREFIX = /^shopify-section-(?:(?:template|sections)--\d+__)?/;
  const SECTION_KEY_SUFFIX = /_(?=[A-Za-z0-9]{6}$)(?=[a-z]*[A-Z0-9])[A-Za-z0-9]{6}$/;
  const TRACKED_VIDEO_OWNERS = '[data-product-video-player], dieux-product-media-gallery';

  let pageSequence = 0;
  let lastGesture = { target: null, at: 0 };

  function now() {
    return performance.now();
  }

  function cleanLabel(value) {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim().substring(0, 100);
  }

  function getTemplateBase() {
    const main = document.getElementById('MainContent');
    const template = main && main.getAttribute('data-template');
    return template ? template.split('.')[0] : '';
  }

  function handleFromUrl(url) {
    if (!url) return '';
    const match = String(url).match(/\/products\/([^?#/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function readPageContext() {
    const context = {
      page_type: getTemplateBase(),
      page_path: window.location.pathname,
      page_title: document.title
    };
    const handle = handleFromUrl(window.location.pathname);
    if (handle) context.product_handle = handle;
    return context;
  }

  let pageContext = readPageContext();
  let pendingEvents = [];
  let drainScheduled = false;
  let sendImmediately = false;

  const scheduleIdle = typeof window.requestIdleCallback === 'function'
    ? (callback) => window.requestIdleCallback(callback, { timeout: 2000 })
    : (callback) => window.setTimeout(callback, 200);

  function dispatchEvent(detail) {
    document.dispatchEvent(new CustomEvent('dieux:analytics-event', { detail: detail }));
  }

  function scheduleDrain() {
    if (drainScheduled) return;
    drainScheduled = true;
    scheduleIdle(drainInIdle);
  }

  function drainInIdle(deadline) {
    drainScheduled = false;
    while (pendingEvents.length) {
      dispatchEvent(pendingEvents.shift());
      if (!deadline || deadline.didTimeout || deadline.timeRemaining() < 8) break;
    }
    if (pendingEvents.length) scheduleDrain();
  }

  function drainPendingEvents() {
    const batch = pendingEvents;
    pendingEvents = [];
    batch.forEach(dispatchEvent);
  }

  function emit(name, params, key) {
    const detail = {
      name: name,
      params: Object.assign({}, pageContext, params),
      key: [name, pageSequence, key || ''].join('|')
    };
    if (sendImmediately) {
      drainPendingEvents();
      dispatchEvent(detail);
      return;
    }
    pendingEvents.push(detail);
    scheduleDrain();
  }

  function sendNow(callback) {
    sendImmediately = true;
    try {
      drainPendingEvents();
      callback();
    } finally {
      sendImmediately = false;
    }
  }

  function sectionKeyFor(sectionEl) {
    return sectionEl && sectionEl.id ? sectionEl.id.replace(SECTION_ID_PREFIX, '') : '';
  }

  function sectionTypeFor(key) {
    return key.replace(SECTION_KEY_SUFFIX, '');
  }

  function sectionParamsFor(el) {
    const sectionEl = el && el.closest ? el.closest('.shopify-section') : null;
    const key = sectionKeyFor(sectionEl);
    return key ? { section_key: key, section_type: sectionTypeFor(key) } : {};
  }

  function rememberGesture(event) {
    lastGesture = { target: event.target, at: now() };
  }

  function recentGestureWithin(el) {
    return Boolean(
      el &&
      lastGesture.target instanceof Node &&
      now() - lastGesture.at <= GESTURE_WINDOW_MS &&
      el.contains(lastGesture.target)
    );
  }

  const sections = new Map();
  let ratioObserver = null;
  let centerObserver = null;
  let mainObserver = null;

  function templateSections() {
    const main = document.getElementById('MainContent');
    if (!main) return [];
    return [...main.children].filter((el) => el.classList.contains('shopify-section'));
  }

  function isTrackedSection(el) {
    return !/sticky/.test(sectionKeyFor(el));
  }

  function sectionEventParams(state) {
    return {
      section_key: state.key,
      section_type: sectionTypeFor(state.key),
      section_position: state.position
    };
  }

  function sendSectionTime(state, force) {
    if (state.visibleMs < TIME_MIN_MS) {
      if (force) state.visibleMs = 0;
      return;
    }
    state.flushes += 1;
    emit('section_time', Object.assign(sectionEventParams(state), {
      visible_ms: Math.round(state.visibleMs / 100) * 100
    }), state.key + '|' + state.flushes);
    state.visibleMs = 0;
  }

  function markSectionViewed(state) {
    state.viewTimer = 0;
    if (state.viewed || !state.visible || document.visibilityState !== 'visible') return;
    state.viewed = true;
    emit('section_view', sectionEventParams(state), state.key);
  }

  function startSectionClock(state) {
    state.since = now();
    if (!state.viewed && !state.viewTimer) {
      state.viewTimer = window.setTimeout(() => markSectionViewed(state), VIEW_MIN_MS);
    }
  }

  function stopSectionClock(state) {
    if (state.viewTimer) {
      window.clearTimeout(state.viewTimer);
      state.viewTimer = 0;
    }
    if (state.since) {
      state.visibleMs += now() - state.since;
      state.since = 0;
    }
  }

  function updateSectionVisibility(state) {
    const visible = state.ratioVisible || state.centerVisible;
    if (visible === state.visible) return;
    state.visible = visible;
    if (document.visibilityState !== 'visible') return;
    if (visible) {
      startSectionClock(state);
    } else {
      stopSectionClock(state);
      sendSectionTime(state, false);
    }
  }

  function handleRatioEntries(entries) {
    entries.forEach((entry) => {
      const state = sections.get(entry.target);
      if (!state) return;
      state.ratioVisible = entry.isIntersecting
        && entry.boundingClientRect.height > 0
        && entry.intersectionRatio >= 0.5;
      updateSectionVisibility(state);
    });
  }

  function handleCenterEntries(entries) {
    entries.forEach((entry) => {
      const state = sections.get(entry.target);
      if (!state) return;
      state.centerVisible = entry.isIntersecting && entry.boundingClientRect.height > 0;
      updateSectionVisibility(state);
    });
  }

  function trackSection(el, position) {
    const existing = sections.get(el);
    if (existing) {
      existing.position = position;
      return;
    }
    sections.set(el, {
      key: sectionKeyFor(el),
      position: position,
      visible: false,
      ratioVisible: false,
      centerVisible: false,
      viewed: false,
      viewTimer: 0,
      since: 0,
      visibleMs: 0,
      flushes: 0
    });
    ratioObserver.observe(el);
    centerObserver.observe(el);
  }

  function untrackSection(el) {
    const state = sections.get(el);
    if (!state) return;
    stopSectionClock(state);
    sendSectionTime(state, true);
    ratioObserver.unobserve(el);
    centerObserver.unobserve(el);
    sections.delete(el);
  }

  function syncSections() {
    const current = templateSections();
    const currentSet = new Set(current);
    [...sections.keys()].forEach((el) => {
      if (!currentSet.has(el) || !el.isConnected) untrackSection(el);
    });
    current.forEach((el, index) => {
      if (isTrackedSection(el)) trackSection(el, index + 1);
    });
  }

  function pauseSections() {
    sections.forEach((state) => {
      stopSectionClock(state);
      sendSectionTime(state, false);
    });
  }

  function resumeSections() {
    sections.forEach((state) => {
      if (state.visible) startSectionClock(state);
    });
  }

  function flushSections() {
    sections.forEach((state) => {
      stopSectionClock(state);
      sendSectionTime(state, true);
    });
  }

  function initSections() {
    if (!('IntersectionObserver' in window)) return;
    const main = document.getElementById('MainContent');
    if (!main) return;
    ratioObserver = new IntersectionObserver(handleRatioEntries, { threshold: [0, 0.5, 1] });
    centerObserver = new IntersectionObserver(handleCenterEntries, {
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    });
    syncSections();
    mainObserver = new MutationObserver(syncSections);
    mainObserver.observe(main, { childList: true });
  }

  const scrollMilestonesSent = new Set();

  function readScrollDepth() {
    const height = document.documentElement.scrollHeight;
    if (!height) return;
    const percent = ((window.scrollY + window.innerHeight) / height) * 100;
    SCROLL_MILESTONES.forEach((milestone) => {
      if (percent < milestone || scrollMilestonesSent.has(milestone)) return;
      scrollMilestonesSent.add(milestone);
      emit('scroll_depth', { percent_scrolled: milestone }, String(milestone));
    });
  }

  function handleScroll() {
    if (getTemplateBase() !== 'product') return;
    if (scrollMilestonesSent.size === SCROLL_MILESTONES.length) return;
    if (window.DieuxFrame && typeof window.DieuxFrame.measure === 'function') {
      window.DieuxFrame.measure('dieux-engagement-scroll-depth', readScrollDepth);
    }
  }

  function optionNameFor(control) {
    if (control.dataset.optionName) return control.dataset.optionName;
    const fieldset = control.closest('[data-variant-selector]');
    const named = fieldset && fieldset.querySelector('[data-option-name]');
    if (named && named.dataset.optionName) return named.dataset.optionName;
    const legend = fieldset && fieldset.querySelector('legend');
    return legend ? cleanLabel(legend.textContent) : '';
  }

  function pickerLocationFor(el) {
    const section = sectionParamsFor(el);
    return section.section_type || '';
  }

  function handleVariantChange(event) {
    const control = event.target;
    if (!(control instanceof Element)) return;
    if (!control.matches('input[type="radio"][data-variant-option], select[data-variant-option-select]')) return;
    const picker = control.closest('variant-picker');
    if (!picker) return;

    const isSelect = control instanceof HTMLSelectElement;
    const selectedOption = isSelect ? control.options[control.selectedIndex] : null;
    const linkedUrl = isSelect
      ? selectedOption && selectedOption.dataset.connectedProductUrl
      : control.dataset.connectedProductUrl;
    const params = {
      product_handle: picker.dataset.productHandle || handleFromUrl(window.location.pathname),
      product_id: picker.dataset.productId || '',
      option_name: optionNameFor(control),
      option_value: cleanLabel(isSelect ? (selectedOption ? selectedOption.textContent : control.value) : control.value),
      selector_type: isSelect ? 'dropdown' : 'button',
      picker_location: pickerLocationFor(picker)
    };

    if (linkedUrl) {
      params.selector_type = 'linked_product';
      params.target_product_handle = handleFromUrl(linkedUrl);
      emit('variant_select', params, params.option_name + '|' + params.option_value);
      return;
    }

    window.setTimeout(() => {
      params.variant_id = picker.dataset.currentVariantId || control.dataset.variantId || '';
      emit('variant_select', params, params.option_name + '|' + params.option_value + '|' + params.variant_id);
    }, 0);
  }

  function handleLinkedProductClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element
      ? event.target.closest('product-link-selector a[data-secondary-product-option]')
      : null;
    if (!link || link.getAttribute('aria-disabled') === 'true') return;
    const targetHandle = handleFromUrl(link.getAttribute('href'));
    const currentHandle = handleFromUrl(window.location.pathname);
    if (!targetHandle || targetHandle === currentHandle) return;
    const selector = link.closest('product-link-selector');
    emit('variant_select', {
      product_handle: currentHandle,
      product_id: (selector && selector.dataset.productId) || '',
      target_product_handle: targetHandle,
      option_value: cleanLabel(link.dataset.optionLabel || link.getAttribute('aria-label') || link.textContent),
      selector_type: 'linked_product',
      picker_location: pickerLocationFor(link)
    }, targetHandle);
  }

  function handleCardChange(event) {
    const card = event.target;
    if (!(card instanceof Element)) return;
    const detail = event.detail || {};
    const selected = card.querySelector('[data-product-card-option][aria-current="true"], [data-product-card-option][aria-pressed="true"]');
    const link = card.querySelector('a[href*="/products/"]');
    emit('variant_select', {
      product_handle: link ? handleFromUrl(link.getAttribute('href')) : '',
      product_id: detail.productId ? String(detail.productId) : '',
      variant_id: detail.variantId ? String(detail.variantId) : '',
      option_value: selected ? cleanLabel(selected.dataset.optionLabel || selected.textContent) : '',
      selector_type: 'card_swatch',
      picker_location: pickerLocationFor(card)
    }, String(detail.productId || '') + '|' + String(detail.variantId || ''));
  }

  const galleries = new WeakMap();

  function activeGallerySlide(gallery) {
    return gallery.querySelector('[data-product-media-slide][aria-hidden="false"]');
  }

  function activeGalleryMediaId(gallery) {
    const slide = activeGallerySlide(gallery);
    if (slide && slide.dataset.mediaId) return slide.dataset.mediaId;
    const thumb = gallery.querySelector('[data-product-media-thumb][aria-current="true"]');
    if (thumb && thumb.dataset.mediaId) return thumb.dataset.mediaId;
    return gallery.dataset.activeMediaId || '';
  }

  function galleryMethodFor(target) {
    if (!(target instanceof Element)) return '';
    if (target.closest('[data-product-media-thumb-scroll]')) return '';
    if (target.closest('[data-product-media-thumb]')) return 'thumbnail';
    if (target.closest('[data-swiper-button]')) return 'arrow';
    return 'swipe';
  }

  function checkGallery(gallery, state) {
    state.queued = false;
    const mediaId = activeGalleryMediaId(gallery);
    if (!mediaId || mediaId === state.mediaId) return;
    state.mediaId = mediaId;
    if (!state.method || now() - state.methodAt > GESTURE_WINDOW_MS) return;

    const slides = [...gallery.querySelectorAll('[data-product-media-slide]')]
      .filter((slide) => !slide.closest('[data-product-media-slide-cache]'));
    const slide = slides.find((candidate) => candidate.dataset.mediaId === mediaId);
    const loopIndex = slide ? parseInt(slide.getAttribute('data-swiper-slide-index'), 10) : NaN;
    emit('gallery_slide_view', {
      media_id: mediaId,
      media_type: (slide && slide.dataset.mediaType) || '',
      slide_position: Number.isNaN(loopIndex) ? (slide ? slides.indexOf(slide) + 1 : 0) : loopIndex + 1,
      slide_count: slides.length,
      gallery_method: state.method
    }, mediaId);
  }

  function trackGallery(gallery) {
    if (galleries.has(gallery)) return;
    const state = { mediaId: activeGalleryMediaId(gallery), method: '', methodAt: 0, queued: false };
    galleries.set(gallery, state);

    gallery.addEventListener('pointerdown', (event) => {
      state.method = galleryMethodFor(event.target);
      state.methodAt = now();
    }, { capture: true, passive: true });
    gallery.addEventListener('keydown', (event) => {
      if (!/^(ArrowLeft|ArrowRight|Home|End)$/.test(event.key)) return;
      state.method = 'keyboard';
      state.methodAt = now();
    }, { capture: true, passive: true });

    new MutationObserver(() => {
      if (state.queued) return;
      state.queued = true;
      window.setTimeout(() => checkGallery(gallery, state), 0);
    }).observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-current', 'data-active-media-id']
    });
  }

  function initGalleries() {
    document.querySelectorAll('dieux-product-media-gallery').forEach(trackGallery);
  }

  const tabSelections = new WeakMap();

  function tabLabel(tab) {
    if (!tab) return '';
    const label = tab.querySelector('.dieux-tabs__label');
    return cleanLabel(
      (label && label.textContent) ||
      tab.getAttribute('data-click-link-name') ||
      tab.getAttribute('data-dieux-tab-label') ||
      tab.textContent
    );
  }

  function selectedTabLabel(tabs) {
    return tabLabel(tabs.querySelector('[role="tab"][aria-selected="true"]'));
  }

  function rememberTabSelection(event) {
    const tabs = event.target instanceof Element ? event.target.closest('dieux-tabs') : null;
    if (tabs) tabSelections.set(tabs, selectedTabLabel(tabs));
  }

  function handleTabChange(event) {
    const tabs = event.target;
    if (!(tabs instanceof Element) || !recentGestureWithin(tabs)) return;
    const detail = event.detail || {};
    const label = tabLabel(detail.tab);
    const previous = tabSelections.get(tabs) || '';
    tabSelections.set(tabs, label);
    emit('tab_change', Object.assign(sectionParamsFor(tabs), {
      tab_label: label,
      tab_value: detail.value || '',
      tab_position: typeof detail.index === 'number' ? detail.index + 1 : 0,
      previous_tab_label: previous
    }), label);
  }

  function handleAccordionClick(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-accordion-trigger]') : null;
    if (!trigger) return;
    window.setTimeout(() => {
      const expanded = trigger.getAttribute('aria-expanded');
      if (expanded !== 'true' && expanded !== 'false') return;
      const labelEl = trigger.querySelector('.accordion__label');
      const label = cleanLabel(labelEl ? labelEl.textContent : trigger.textContent);
      const state = expanded === 'true' ? 'open' : 'close';
      emit('accordion_toggle', Object.assign(sectionParamsFor(trigger), {
        accordion_label: label,
        accordion_state: state
      }), label + '|' + state + '|' + Math.round(now()));
    }, 0);
  }

  let stickySent = false;
  let stickyObserver = null;

  function checkSticky(sticky) {
    if (stickySent || sticky.dataset.stickyState !== 'visible') return;
    stickySent = true;
    emit('sticky_atc_view', {
      variant_id: sticky.dataset.variant || '',
      product_id: sticky.dataset.clickProductid || ''
    });
  }

  function initSticky() {
    if (stickyObserver) stickyObserver.disconnect();
    const sticky = document.querySelector('sticky-add-to-cart[data-sticky-state]');
    if (!sticky) return;
    stickyObserver = new MutationObserver(() => checkSticky(sticky));
    stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['data-sticky-state'] });
    checkSticky(sticky);
  }

  let videoStates = new WeakMap();

  function trackedVideo(event) {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return null;
    const owner = video.closest(TRACKED_VIDEO_OWNERS);
    return owner ? { video: video, owner: owner } : null;
  }

  function videoStateFor(video) {
    let state = videoStates.get(video);
    if (!state) {
      state = { started: false, completed: false, milestones: new Set(), lastPercent: 0 };
      videoStates.set(video, state);
    }
    return state;
  }

  function videoParams(tracked, extra) {
    const video = tracked.video;
    const player = video.closest('video-player-component');
    const slide = video.closest('[data-product-media-slide]');
    const duration = Number.isFinite(video.duration) ? Math.round(video.duration) : 0;
    return Object.assign(sectionParamsFor(video), {
      video_provider: (player && player.dataset.videoProvider) || 'html5',
      video_title: cleanLabel(video.getAttribute('aria-label') || video.getAttribute('title') || (slide ? 'Gallery video' : 'Product video')),
      video_location: slide ? 'gallery' : 'product_video',
      media_id: slide ? slide.dataset.mediaId || '' : '',
      video_duration: duration,
      video_current_time: Math.round(video.currentTime || 0),
      video_initiator: player && recentGestureWithin(player) ? 'user' : 'autoplay'
    }, extra);
  }

  function completeVideo(tracked, state) {
    if (state.completed) return;
    state.completed = true;
    emit('video_complete', videoParams(tracked, { video_percent: 100 }), 'complete');
  }

  function handleVideoPlay(event) {
    const tracked = trackedVideo(event);
    if (!tracked) return;
    const state = videoStateFor(tracked.video);
    if (state.started) return;
    state.started = true;
    emit('video_start', videoParams(tracked, { video_percent: 0 }), 'start');
  }

  function handleVideoTime(event) {
    const tracked = trackedVideo(event);
    if (!tracked) return;
    const video = tracked.video;
    const state = videoStateFor(video);
    if (!state.started || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const percent = (video.currentTime / video.duration) * 100;
    if (state.lastPercent >= 90 && percent < 10) completeVideo(tracked, state);
    state.lastPercent = percent;
    VIDEO_MILESTONES.forEach((milestone) => {
      if (percent < milestone || state.milestones.has(milestone)) return;
      state.milestones.add(milestone);
      emit('video_progress', videoParams(tracked, { video_percent: milestone }), String(milestone));
    });
  }

  function handleVideoEnded(event) {
    const tracked = trackedVideo(event);
    if (tracked) completeVideo(tracked, videoStateFor(tracked.video));
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') sendNow(pauseSections);
    else resumeSections();
  }

  function handlePageHide() {
    sendNow(flushSections);
  }

  function handleVirtualPageView() {
    sendNow(flushSections);
    pageSequence += 1;
    pageContext = readPageContext();
    sections.forEach((state) => {
      state.viewed = false;
      state.flushes = 0;
      if (state.visible && document.visibilityState === 'visible') startSectionClock(state);
    });
    scrollMilestonesSent.clear();
    stickySent = false;
    videoStates = new WeakMap();
    window.setTimeout(() => {
      syncSections();
      initGalleries();
      initSticky();
    }, 0);
  }

  function init() {
    initSections();
    initGalleries();
    initSticky();

    document.addEventListener('pointerdown', rememberGesture, { capture: true, passive: true });
    document.addEventListener('keydown', rememberGesture, { capture: true, passive: true });
    document.addEventListener('pointerdown', rememberTabSelection, { capture: true, passive: true });
    document.addEventListener('keydown', rememberTabSelection, { capture: true, passive: true });
    document.addEventListener('change', handleVariantChange, true);
    document.addEventListener('click', handleLinkedProductClick, true);
    document.addEventListener('click', handleAccordionClick, true);
    document.addEventListener('product-card:change', handleCardChange);
    document.addEventListener('dieux:tabs:change', handleTabChange);
    document.addEventListener('dieux:virtual-page-view', handleVirtualPageView);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('scroll', handleScroll, { passive: true });

    if (getTemplateBase() === 'product') {
      document.addEventListener('play', handleVideoPlay, true);
      document.addEventListener('timeupdate', handleVideoTime, true);
      document.addEventListener('ended', handleVideoEnded, true);
    }
  }

  function start() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', start, { once: true });
  } else {
    start();
  }
})();
